import * as React from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { displayLocaleTime, getLastDayOfMonth, getMonthName } from 'gossamer-unified';
import classes from './MobileCalendar.module.css';

export interface EventIndicator {
  date: Date;
  colour: string;
}
export interface MobileCalendarProps {
  indicators: EventIndicator[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

export const MobileCalendar = (props: MobileCalendarProps): React.JSX.Element => {
  const children = [];
  for (let day = 1; day <= getLastDayOfMonth(props.selectedDate); day++) {
    const date = new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), day);
    const dayIndicators = props.indicators
      .filter(
        (indicator) =>
          indicator.date.getFullYear() === date.getFullYear() &&
          indicator.date.getMonth() === date.getMonth() &&
          indicator.date.getDate() === date.getDate(),
      )
      .filter((_, index) => index < 4) // can't fit more than 4
      .map(
        (
          indicator,
          index, // index is okay as order shouldn't change
        ) => (
          <span key={index} style={{ color: indicator.colour }}>
            •
          </span>
        ),
      );
    const dayClasses = [getDayClass(date)];
    if (props.selectedDate.getDate() === day) {
      dayClasses.push(classes.selected);
    }
    children.push(
      <div key={day} className={dayClasses.join(' ')} onClick={() => props.setSelectedDate(date)}>
        <div className={classes.DayNumber}>{day}</div>
        <div className={classes.IndicatorSet}>{dayIndicators}</div>
      </div>,
    );
  }
  return (
    <div className={classes.MobileCalendarGrid}>
      <div className={[classes.mon, classes.header].join(' ')}>Mon</div>
      <div className={[classes.tue, classes.header].join(' ')}>Tue</div>
      <div className={[classes.wed, classes.header].join(' ')}>Wed</div>
      <div className={[classes.thu, classes.header].join(' ')}>Thu</div>
      <div className={[classes.fri, classes.header].join(' ')}>Fri</div>
      <div className={[classes.sat, classes.header].join(' ')}>Sat</div>
      <div className={[classes.sun, classes.header].join(' ')}>Sun</div>
      {children}
    </div>
  );
};

const getDayClass = (date: Date): string => {
  switch (date.getDay()) {
    case 0:
      return classes.sun;
    case 1:
      return classes.mon;
    case 2:
      return classes.tue;
    case 3:
      return classes.wed;
    case 4:
      return classes.thu;
    case 5:
      return classes.fri;
    case 6:
      return classes.sat;
    default:
      return '';
  }
};

export interface MobileCalendarHeaderProps {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

export const MobileCalendarHeader = (props: MobileCalendarHeaderProps): React.JSX.Element => {
  const today = new Date();
  const isTodaySelected =
    props.selectedDate.getFullYear() === today.getFullYear() &&
    props.selectedDate.getMonth() === today.getMonth() &&
    props.selectedDate.getDate() === today.getDate();
  return (
    <div className={classes.MobileCalendarHeader}>
      <div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() =>
            props.setSelectedDate(new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth() - 1, 1))
          }
        >
          « Prev
        </Button>
      </div>
      <div className={classes.DayLabel}>
        {getMonthName(props.selectedDate)} {props.selectedDate.getFullYear()}
      </div>
      <div>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={isTodaySelected}
          onClick={() => props.setSelectedDate(new Date())}
        >
          Today
        </Button>
      </div>
      <div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() =>
            props.setSelectedDate(new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth() + 1, 1))
          }
        >
          Next »
        </Button>
      </div>
    </div>
  );
};

export interface MobileCalendarEventProps {
  title: string;
  startAt: Date;
  endAt: Date;
  colorCode?: string;
  navLink?: string;
}

export const MobileCalendarEvent = (props: MobileCalendarEventProps): React.JSX.Element => {
  return (
    <Link to={props.navLink || '#'} className={classes.MobileCalendarEvent}>
      <div style={{ backgroundColor: props.colorCode || 'transparent' }}></div>
      <div>{displayLocaleTime(props.startAt)}</div>
      <div>{displayLocaleTime(props.endAt)}</div>
      <div>{props.title}</div>
    </Link>
  );
};
